/**
 * Centralized Date Service
 * 
 * This module provides date/time formatting and timezone management
 * for user-facing displays.
 *
 * Use `convertToUserTimezone` for any UI dates.
 * Direct `dayjs` usage is OK for API params or calculations that are
 * not user-facing.
 *
 * Usage:
 *   import { convertToUserTimezone, formatRelativeTime } from "../utils/dateService";
 */

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import relativeTime from "dayjs/plugin/relativeTime";
import isBetween from "dayjs/plugin/isBetween";
import customParseFormat from "dayjs/plugin/customParseFormat";

// Initialize dayjs plugins once
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
dayjs.extend(isBetween);
dayjs.extend(customParseFormat);


// ============================================================================
// Timezone Management
// ============================================================================

export const ADMIN_TIMEZONE_STORAGE_KEY = "pam_admin_timezone";
export const DEFAULT_ADMIN_TIMEZONE = "America/New_York";
export const DEFAULT_STAFF_TIMEZONE = "UTC";

/**
 * Get the stored admin timezone from localStorage
 * @returns {string | null} The stored timezone or null
 */
export const getStoredAdminTimezone = () => {
  try {
    const stored = localStorage.getItem(ADMIN_TIMEZONE_STORAGE_KEY);
    return stored || null;
  } catch {
    return null;
  }
};

/**
 * Store the admin timezone in localStorage
 * @param {string | null} tz - The timezone to store, or null to clear
 */
export const setStoredAdminTimezone = (tz) => {
  try {
    if (!tz) {
      localStorage.removeItem(ADMIN_TIMEZONE_STORAGE_KEY);
      return;
    }
    localStorage.setItem(ADMIN_TIMEZONE_STORAGE_KEY, tz);
  } catch {
    // ignore localStorage errors
  }
};

/**
 * Get the effective admin timezone (stored or default EST fallback)
 * @returns {string} The timezone to use for formatting
 */
export const getEffectiveAdminTimezone = () =>
  getStoredAdminTimezone() || DEFAULT_ADMIN_TIMEZONE;

export const getAdminNow = () => dayjs().tz(getEffectiveAdminTimezone());

/**
 * Minimal IANA fallback list. We only fall back to this if the runtime
 * doesn't expose `Intl.supportedValuesOf("timeZone")` (which has been
 * supported in evergreen browsers since 2022 and Node >= 18).
 */
const FALLBACK_IANA_TIMEZONES = Object.freeze([
  "UTC",
  "America/New_York",
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Madrid",
  "Europe/Berlin",
  "Europe/Athens",
  "Africa/Lagos",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Shanghai",
  "Asia/Tokyo",
  "Australia/Sydney",
  "Pacific/Auckland",
]);

/**
 * Return the canonical IANA timezone identifiers known to the runtime.
 * Use this to populate timezone pickers; pair with `formatTimezoneDisplay`
 * to render a DST-aware label.
 *
 * `Intl.supportedValuesOf("timeZone")` (per ECMA-402) does not include the
 * `"UTC"` alias on every runtime, so we always prepend it.
 *
 * @returns {string[]} Sorted IANA timezone identifiers
 */
export const getAvailableTimezones = () => {
  let zones = [...FALLBACK_IANA_TIMEZONES];

  if (typeof Intl?.supportedValuesOf === "function") {
    try {
      zones = Intl.supportedValuesOf("timeZone");
    } catch (error) {
      // Non-critical: fall back to the static list and surface the cause
      // for diagnostics in environments where Intl misbehaves.
      console.warn("getAvailableTimezones: Intl.supportedValuesOf threw, using fallback list", error);
    }
  }

  return zones.includes("UTC") ? zones : ["UTC", ...zones];
};

// ============================================================================
// Date Parsing Utilities
// ============================================================================

/**
 * Parse a timestamp that could be in seconds or milliseconds
 * Preserves fractional seconds up to millisecond resolution.
 * @param {number} timestamp - Unix timestamp
 * @returns {dayjs.Dayjs} Parsed dayjs object
 */
export const parseUnixTimestamp = (timestamp) => {
  // If timestamp is less than 1e12, it's in seconds, otherwise milliseconds.
  // Use millisecond conversion for seconds to preserve fractional precision.
  return timestamp < 1e12 ? dayjs(timestamp * 1000) : dayjs(timestamp);
};

/**
 * Parse a timestamp string or number into a dayjs object
 * Handles both date strings (e.g., "2024-12-11 21:19:05") and Unix timestamps
 * @param {string | number} timestampString - The timestamp to parse
 * @returns {dayjs.Dayjs} Parsed dayjs object
 */
export const parseTimestampString = (timestampString) => {
  // Check if it's a Unix timestamp (numeric string or number)
  if (
    typeof timestampString === "number" ||
    (typeof timestampString === "string" &&
      /^\d+(?:\.\d+)?$/.test(timestampString.trim()))
  ) {
    const timestamp = parseFloat(timestampString);
    return parseUnixTimestamp(timestamp);
  }
  // Parse as date string (handles formats like "2024-12-11 21:19:05")
  return dayjs(timestampString);
};

/**
 * Normalize a date input to milliseconds
 * @param {string | number | Date} date - The date to normalize
 * @returns {number | string | Date} Normalized date value
 */
const normalizeToMilliseconds = (date) => {
  if (typeof date === "number" || (typeof date === "string" && !isNaN(date))) {
    const num = Number(date);
    // If the timestamp is larger than 1e12, it's already in milliseconds
    return num > 1e12 ? Math.floor(num) : Math.floor(num * 1000);
  }
  return date;
};

/** API dates: UTC wall time without offset (optional fractional seconds). */
const BACKEND_UTC_FORMATS = ["YYYY-MM-DD HH:mm:ss.SSS", "YYYY-MM-DD HH:mm:ss"];

/** Calendar date with no time-of-day; has no instant to shift between zones. */
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Withdrawal display strings that are UTC wall clock without a TZ marker. */
const UTC_DISPLAY_FORMATS = ["MMM DD, YYYY HH:mm", "MMM DD, HH:mm"];

/**
 * Parse `date` as an absolute instant in UTC for {@link convertToUserTimezone}.
 * Primary shape from the API: {@link BACKEND_UTC_FORMATS} (naive SQL = UTC wall).
 * Also accepts Unix ms/s, `Date`, and ISO with `Z` / offset.
 *
 * Call sites whose BE payload is already admin-TZ must use
 * {@link formatDateNoTimezone} instead of this path.
 *
 * @param {string | number | Date} date
 * @returns {import("dayjs").Dayjs}
 */
const parseUtcInstant = (date) => {
  const normalized = normalizeToMilliseconds(date);

  if (typeof normalized === "number") {
    return dayjs.utc(normalized);
  }

  if (normalized instanceof Date) {
    return dayjs.utc(normalized);
  }

  if (typeof normalized === "string") {
    const s = normalized.trim();

    if (/^\d+(?:\.\d+)?$/.test(s)) {
      const num = parseFloat(s);
      return num < 1e12 ? dayjs.utc(num * 1000) : dayjs.utc(num);
    }

    for (const candidate of [s, s.replace("T", " ")]) {
      for (const fmt of BACKEND_UTC_FORMATS) {
        const d = dayjs.utc(candidate, fmt, true);
        if (d.isValid()) return d;
      }
    }

    if (/[zZ]$/.test(s) || /[+-]\d{2}:\d{2}$/.test(s) || /[+-]\d{4}$/.test(s)) {
      return dayjs(s).utc();
    }

    return dayjs.utc(s);
  }

  return dayjs.utc(normalized);
};

// ============================================================================
// Core Formatting Functions
// ============================================================================

/**
 * Convert a date to the admin's timezone and format it
 * This is the PRIMARY function for displaying UTC instants in the app
 *
 * Backend contract: UTC as unix, `Date`, ISO with `Z` / offset, or naive
 * `"YYYY-MM-DD HH:mm:ss"` (UTC wall, no suffix — e.g. Athena / some affiliates).
 * Date-only `"YYYY-MM-DD"` is a calendar bucket and is formatted without a shift.
 * Values already localized to the admin TZ must use {@link formatDateNoTimezone}.
 *
 * @param {string | number | Date} date - The date to format
 * @param {string} format - dayjs format string (default: "MMM DD, YYYY HH:mm")
 * @param {string} userTimezone - Override timezone (default: admin's effective timezone)
 * @returns {string} Formatted date string
 */
export const convertToUserTimezone = (
  date,
  format = "MMM DD, YYYY HH:mm",
  userTimezone = getEffectiveAdminTimezone()
) => {
  if (date == null || date === "") return "-";

  // Date-only values (e.g. "2026-05-01") are calendar dates, not instants.
  // Formatting them through a timezone would shift the day, so format as-is.
  if (typeof date === "string" && DATE_ONLY_RE.test(date.trim())) {
    const d = dayjs(date.trim(), "YYYY-MM-DD", true);
    return d.isValid() ? d.format(format) : "-";
  }

  const instant = parseUtcInstant(date);
  if (!instant.isValid()) return "";

  return instant.tz(userTimezone).format(format);
};

/**
 * Prefer a UTC instant companion (`*_timestamp` / ISO / unix) via
 * {@link convertToUserTimezone}. Withdrawal display strings like
 * `"Apr 01, 2024 09:00"` are UTC wall without a marker — parse as UTC and
 * shift; do not passthrough (that would show UTC as if it were admin-local).
 *
 * @param {string | number | null | undefined} timestamp
 * @param {string | null | undefined} displayFallback
 * @param {string} [format]
 * @param {string} [userTimezone]
 * @returns {string}
 */
export const formatTimestampOrDisplay = (
  timestamp,
  displayFallback = "",
  format = "MMM DD, YYYY HH:mm",
  userTimezone = getEffectiveAdminTimezone(),
) => {
  if (timestamp != null && timestamp !== "") {
    return convertToUserTimezone(timestamp, format, userTimezone);
  }
  if (displayFallback == null || displayFallback === "") return "";

  const display = String(displayFallback).trim();
  // Keep wall-clock digits as UTC (these strings have no Z/offset marker).
  const utcDisplay = dayjs(display, UTC_DISPLAY_FORMATS, true);
  if (utcDisplay.isValid()) {
    return dayjs
      .utc(utcDisplay.format("YYYY-MM-DD HH:mm:ss"))
      .tz(userTimezone)
      .format(format);
  }

  // Unknown display shape — still try the UTC converter (naive SQL, ISO, …).
  return convertToUserTimezone(display, format, userTimezone);
};

/**
 * Format a timezone label for account/profile UI.
 *
 * @param {string} userTimezone - IANA timezone name
 * @param {Date | string | number} referenceDate - Date used to compute the live offset
 * @returns {string} Formatted timezone label
 */
export const formatTimezoneDisplay = (
  userTimezone = getEffectiveAdminTimezone(),
  referenceDate = new Date()
) => {
  const effectiveTimezone = userTimezone || getEffectiveAdminTimezone();

  let utcOffset = "";
  try {
    utcOffset = convertToUserTimezone(referenceDate, "Z", effectiveTimezone);
  } catch (error) {
    // Non-critical: dayjs throws on unknown IANA names. We render the raw
    // value below; warn so unexpected backend values are visible in dev.
    console.warn(`formatTimezoneDisplay: invalid IANA timezone "${effectiveTimezone}"`, error);
  }

  if (!utcOffset) {
    return effectiveTimezone;
  }

  if (
    effectiveTimezone === "UTC" ||
    effectiveTimezone === "Etc/UTC" ||
    effectiveTimezone === "GMT"
  ) {
    return `UTC (UTC${utcOffset})`;
  }

  return `${effectiveTimezone} (UTC${utcOffset})`;
};

/**
 * Format a date WITHOUT timezone conversion (uses local time)
 * Use sparingly - most dates should use convertToUserTimezone
 * 
 * @param {string | number | Date} date - The date to format
 * @param {string} format - dayjs format string
 * @returns {string} Formatted date string
 */
export const formatDateNoTimezone = (date, format = "D MMMM YYYY, HH:mm:ss") => {
  if (date == null || date === "") return "";
  
  const parsedDate = normalizeToMilliseconds(date);
  return dayjs(parsedDate).format(format);
};

/**
 * Format a date to UTC
 * 
 * @param {string | number | Date} date - The date to format
 * @param {string} format - dayjs format string
 * @returns {string} Formatted date string in UTC
 */
export const toUTC = (date, format = "YYYY-MM-DD HH:mm:ss") => {
  if (date == null || date === "") return "";
  
  const parsedDate = normalizeToMilliseconds(date);
  return dayjs(parsedDate).utc().format(format);
};

// ============================================================================
// Convenience Formatting Functions
// ============================================================================

/**
 * Format an array of dates with hour (uses admin timezone)
 * 
 * @param {string[]} dates - Array of date strings
 * @returns {string[]} Array of formatted dates
 */
export const formatDateArrayWithHour = (dates) => {
  if (!dates || !Array.isArray(dates)) return [];
  
  return dates.map((date) => convertToUserTimezone(date, "D MMM, HH:mm"));
};

/**
 * Format a date with a specific format string (local time, no timezone conversion)
 * Replacement for date-fns format()
 * 
 * @param {Date | string | number} date - The date to format
 * @param {string} formatStr - dayjs format string (e.g., "YYYY-MM-DD", "MMM")
 * @returns {string} Formatted date string
 */
export const formatDate = (date, formatStr) => {
  if (!date) return "";
  return dayjs(date).format(formatStr);
};

/**
 * Get the year from a date
 * Replacement for date-fns getYear()
 * 
 * @param {Date | string | number} date - The date
 * @returns {number} The year
 */
export const getYear = (date) => {
  return dayjs(date).year();
};

/**
 * Parse a date string with a specific format
 * Replacement for date-fns parse()
 * 
 * @param {string} dateString - The date string to parse
 * @param {string} formatStr - The format of the input string (e.g., "YYYY-MM")
 * @returns {dayjs.Dayjs} Parsed dayjs object
 */
export const parseDate = (dateString, formatStr) => {
  return dayjs(dateString, formatStr);
};

/**
 * Format relative time (e.g., "3 days ago", "in 2 hours")
 * Replacement for date-fns formatDistanceToNow()
 * 
 * @param {Date | string | number} date - The date to compare
 * @param {Object} options - Options
 * @param {boolean} options.addSuffix - Add suffix like "ago" or "in" (default: true)
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (date, { addSuffix = true } = {}) => {
  if (!date) return "";
  return dayjs(date).fromNow(!addSuffix);
};

/**
 * Check if a date is within a date range (inclusive)
 * Replacement for date-fns isWithinInterval()
 * 
 * @param {Date | string | number} date - The date to check
 * @param {Date | string | number} start - Start of range
 * @param {Date | string | number} end - End of range
 * @returns {boolean} True if date is within the range
 */
export const isDateInRange = (date, start, end) => {
  if (!date || !start || !end) return false;
  return dayjs(date).isBetween(dayjs(start), dayjs(end), null, "[]");
};

// ============================================================================
// Default Export (all functions)
// ============================================================================

const dateService = {
  // Timezone management
  ADMIN_TIMEZONE_STORAGE_KEY,
  DEFAULT_ADMIN_TIMEZONE,
  DEFAULT_STAFF_TIMEZONE,
  getStoredAdminTimezone,
  setStoredAdminTimezone,
  getEffectiveAdminTimezone,
  getAdminNow,
  getAvailableTimezones,
  
  // Parsing
  parseUnixTimestamp,
  parseTimestampString,
  parseDate,
  
  // Core formatting
  convertToUserTimezone,
  formatTimestampOrDisplay,
  formatTimezoneDisplay,
  formatDateNoTimezone,
  toUTC,
  
  // Convenience formatting
  formatDateArrayWithHour,
  formatDate,
  getYear,
  formatRelativeTime,
  isDateInRange,
};

export default dateService;
